<template>
  <div class="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
    <aside class="sticky top-6 self-start rounded-xl border border-gray-200 bg-white p-4 shadow-sm h-[calc(100vh-140px)]">
      <h3 class="text-sm font-semibold text-gray-900">
        Blocks
      </h3>
      <p class="mt-1 text-xs text-gray-500">
        Drag blocks into the page.
      </p>
      <div class="mt-4 space-y-2 overflow-y-auto pr-2 max-h-[calc(100vh-220px)]">
        <div
          v-for="block in blockOptions"
          :key="block.value"
          class="flex w-full cursor-grab items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-primary hover:bg-primary/5"
          draggable="true"
          @dragstart="onDragStart($event, block.value)"
        >
          <span class="flex items-center gap-2">
            <span class="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-600">
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path :d="iconPath(block.value)" />
              </svg>
            </span>
            {{ block.label }}
          </span>
          <span class="text-xs text-gray-400">Drag</span>
        </div>
      </div>
    </aside>

    <section
      class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-900">
          Page Canvas
        </h3>
        <div class="text-xs text-gray-500">
          {{ blocks.length }} block(s)
        </div>
      </div>

      <div
        v-if="blocks.length === 0"
        class="mt-4 rounded-xl border-2 border-dashed border-gray-400 bg-white p-8 text-center text-sm text-gray-500"
      >
        Add your first block from the left panel.
      </div>

      <div
        v-else
        class="mt-4 space-y-4 min-h-[160px]"
        @dragover.prevent="onCanvasDragOver"
        @drop.prevent="onCanvasDrop"
      >
        <div
          v-for="(element, index) in blocks"
          :key="element.id"
          class="group relative rounded-xl border border-transparent bg-transparent p-4 transition hover:border-2 hover:border-dashed hover:border-primary/50"
          :class="{
            'border-primary/50': (dropIndex === index || dropIndex === index + 1) && draggingIndex !== index,
          }"
          data-block="true"
          draggable="true"
          @dragstart="onBlockDragStart($event, index)"
          @dragend="onBlockDragEnd"
          @dragover.prevent="onBlockDragOver($event, index)"
          @dragleave="dropIndex = null"
          @drop.prevent="onBlockDrop($event, index)"
          @click="selectBlock(element.id)"
        >
          <div
            v-if="dropIndex === index && draggingIndex !== index"
            class="pointer-events-none absolute -top-2 left-0 right-0 h-0.5 rounded-full bg-primary/60"
          />
          <div
            v-if="dropIndex === index + 1 && draggingIndex !== index"
            class="pointer-events-none absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-primary/60"
          />
          <div
            class="absolute right-4 top-4 flex items-center gap-2 opacity-0 transition group-hover:opacity-100"
            data-drag-hide="true"
          >
            <button
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 drag-handle"
              type="button"
              title="Drag"
              draggable="true"
              @dragstart="onBlockDragStart($event, index)"
              @dragend="onBlockDragEnd"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle
                  cx="9"
                  cy="7"
                  r="1.5"
                />
                <circle
                  cx="15"
                  cy="7"
                  r="1.5"
                />
                <circle
                  cx="9"
                  cy="12"
                  r="1.5"
                />
                <circle
                  cx="15"
                  cy="12"
                  r="1.5"
                />
                <circle
                  cx="9"
                  cy="17"
                  r="1.5"
                />
                <circle
                  cx="15"
                  cy="17"
                  r="1.5"
                />
              </svg>
            </button>
            <button
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              type="button"
              title="Move up"
              @click.stop="moveBlock(index, -1)"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 5v14" />
                <path d="M6 11l6-6 6 6" />
              </svg>
            </button>
            <button
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              type="button"
              title="Move down"
              @click.stop="moveBlock(index, 1)"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 5v14" />
                <path d="M18 13l-6 6-6-6" />
              </svg>
            </button>
            <button
              class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 hover:bg-red-50"
              type="button"
              title="Remove"
              @click.stop="removeBlock(index)"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4h8v2" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M5 6l1 14h12l1-14" />
              </svg>
            </button>
          </div>

          <div
            class="space-y-3 text-gray-900"
            data-drag-preview="true"
          >
            <div
              v-if="element.type === 'heading'"
              :class="previewAlign(element.data.align)"
            >
              <input
                v-model="element.data.text"
                :placeholder="getString(element.data.text, 'Heading')"
                class="w-full bg-transparent focus:outline-none"
                :class="[headingClass(element.data.level), inputAlignClass(element.data.align)]"
                type="text"
              >
            </div>
            <div
              v-else-if="element.type === 'text'"
              :class="previewAlign(element.data.align)"
            >
              <textarea
                v-model="element.data.text"
                class="w-full bg-transparent text-sm text-gray-700 focus:outline-none"
                :class="inputAlignClass(element.data.align)"
                rows="2"
                :placeholder="getString(element.data.text, 'Text block')"
              />
            </div>
            <div v-else-if="element.type === 'image'">
              <div
                v-if="!element.data.src"
                class="flex min-h-[120px] items-center justify-center rounded-lg bg-gray-50/40 text-xs text-gray-400"
              >
                Image preview
              </div>
              <img
                v-else
                :src="element.data.src"
                :alt="getString(element.data.alt, 'Image')"
                class="w-full rounded-lg border border-gray-200"
              >
              <p class="text-xs text-gray-500 mt-2">
                {{ getString(element.data.caption, 'Image caption') }}
              </p>
            </div>
            <div
              v-else-if="element.type === 'button'"
              :class="previewAlign(element.data.align)"
            >
              <span
                class="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold"
                :class="buttonPreviewClass(element.data.variant)"
              >
                {{ getString(element.data.text, 'Button') }}
              </span>
            </div>
            <div v-else-if="element.type === 'quote'">
              <blockquote class="border-l-4 border-primary/40 pl-4 text-sm text-gray-700 italic">
                {{ getString(element.data.text, 'Quote') }}
                <cite class="mt-2 block text-xs text-gray-500">— {{ getString(element.data.author, 'Author') }}</cite>
              </blockquote>
            </div>
            <div v-else-if="element.type === 'embed'">
              <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-xs text-gray-500">
                {{ getString(element.data.url, 'YouTube embed') }}
              </div>
            </div>
            <div v-else-if="element.type === 'divider'">
              <div
                :class="dividerPreviewClass(element.data.style)"
                :style="{ marginTop: `${element.data.spacing || 24}px`, marginBottom: `${element.data.spacing || 24}px` }"
              />
            </div>
            <div v-else-if="element.type === 'html'">
              <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-xs text-gray-500">
                {{ getString(element.data.html, 'Custom HTML block') }}
              </div>
            </div>
            <div
              v-else-if="element.type === 'hero'"
              :class="previewAlign(element.data.align)"
            >
              <div class="space-y-2">
                <div class="text-2xl font-semibold">
                  {{ getString(element.data.headline, 'Hero headline') }}
                </div>
                <div class="text-sm text-gray-600">
                  {{ getString(element.data.subheadline, 'Hero subheadline') }}
                </div>
                <span class="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold bg-black text-white">
                  {{ getString(element.data.ctaText, 'Get Started') }}
                </span>
              </div>
            </div>
            <div
              v-else-if="element.type === 'columns'"
              class="space-y-3"
            >
              <div
                class="grid gap-4 text-sm text-gray-700"
                :class="columnsGridClass(element)"
                :style="{ gap: `${getColumnsGap(element)}px` }"
              >
                <div
                  v-for="(column, colIndex) in getColumns(element)"
                  :key="`col-${element.id}-${colIndex}`"
                  class="rounded-lg border-2 border-dashed border-gray-300 bg-transparent p-3"
                  data-column-inner="true"
                  @dragover.prevent
                  @drop.prevent="onColumnDrop($event, element.id, colIndex)"
                >
                  <div
                    v-if="column.length === 0"
                    class="text-xs text-gray-400"
                  >
                    Drop blocks here
                  </div>
                  <div
                    v-else
                    class="space-y-3"
                  >
                    <div
                      v-for="(child, childIndex) in column"
                      :key="child.id"
                      class="group relative rounded-lg border border-transparent bg-transparent p-3 transition hover:border-2 hover:border-dashed hover:border-primary/50"
                      draggable="true"
                      data-column-inner="true"
                      @dragstart.stop="onColumnBlockDragStart($event, element.id, colIndex, childIndex)"
                      @dragend="onBlockDragEnd"
                      @dragover.prevent="onColumnBlockDragOver($event, element.id, colIndex, childIndex)"
                      @drop.prevent="onColumnBlockDrop($event, element.id, colIndex, childIndex)"
                      @click.stop="selectBlock(child.id)"
                    >
                      <div
                        class="absolute right-3 top-3 flex items-center gap-2 opacity-0 transition group-hover:opacity-100"
                        data-drag-hide="true"
                      >
                        <button
                          class="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                          type="button"
                          title="Move up"
                          @click.stop="moveColumnBlockButton(element.id, colIndex, childIndex, -1)"
                        >
                          <svg
                            class="h-3.5 w-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path d="M12 5v14" />
                            <path d="M6 11l6-6 6 6" />
                          </svg>
                        </button>
                        <button
                          class="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                          type="button"
                          title="Move down"
                          @click.stop="moveColumnBlockButton(element.id, colIndex, childIndex, 1)"
                        >
                          <svg
                            class="h-3.5 w-3.5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path d="M12 5v14" />
                            <path d="M18 13l-6 6-6-6" />
                          </svg>
                        </button>
                        <button
                          class="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                          type="button"
                          title="Remove"
                          @click.stop="removeColumnBlock(element.id, colIndex, childIndex)"
                        >
                          <svg
                            class="h-4 w-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path d="M3 6h18" />
                            <path d="M8 6V4h8v2" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M5 6l1 14h12l1-14" />
                          </svg>
                        </button>
                      </div>
                      <div
                        class="space-y-2 text-gray-900"
                        data-drag-preview="true"
                      >
                        <div
                          v-if="child.type === 'heading'"
                          :class="previewAlign(child.data.align)"
                        >
                          <input
                            v-model="child.data.text"
                            :placeholder="getString(child.data.text, 'Heading')"
                            class="w-full bg-transparent focus:outline-none"
                            :class="[headingClass(child.data.level), inputAlignClass(child.data.align)]"
                            type="text"
                          >
                        </div>
                        <div
                          v-else-if="child.type === 'text'"
                          :class="previewAlign(child.data.align)"
                        >
                          <textarea
                            v-model="child.data.text"
                            class="w-full bg-transparent text-sm text-gray-700 focus:outline-none"
                            :class="inputAlignClass(child.data.align)"
                            rows="2"
                            :placeholder="getString(child.data.text, 'Text block')"
                          />
                        </div>
                        <div v-else-if="child.type === 'image'">
                          <div
                            v-if="!child.data.src"
                            class="flex min-h-[80px] items-center justify-center rounded-lg bg-gray-50 text-xs text-gray-400"
                          >
                            Image preview
                          </div>
                          <img
                            v-else
                            :src="child.data.src"
                            :alt="getString(child.data.alt, 'Image')"
                            class="w-full rounded-lg border border-gray-200"
                          >
                        </div>
                        <div
                          v-else-if="child.type === 'button'"
                          :class="previewAlign(child.data.align)"
                        >
                          <span
                            class="inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold"
                            :class="buttonPreviewClass(child.data.variant)"
                          >
                            {{ getString(child.data.text, 'Button') }}
                          </span>
                        </div>
                        <div v-else-if="child.type === 'quote'">
                          <blockquote class="border-l-2 border-primary/40 pl-3 text-xs text-gray-700 italic">
                            {{ getString(child.data.text, 'Quote') }}
                          </blockquote>
                        </div>
                        <div v-else-if="child.type === 'embed'">
                          <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-center text-[11px] text-gray-500">
                            {{ getString(child.data.url, 'Embed') }}
                          </div>
                        </div>
                        <div v-else-if="child.type === 'divider'">
                          <div
                            :class="dividerPreviewClass(child.data.style)"
                            :style="{ marginTop: `${child.data.spacing || 16}px`, marginBottom: `${child.data.spacing || 16}px` }"
                          />
                        </div>
                        <div v-else-if="child.type === 'html'">
                          <div class="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-[11px] text-gray-500">
                            {{ getString(child.data.html, 'HTML block') }}
                          </div>
                        </div>
                        <div v-else-if="child.type === 'pricing'">
                          <div class="text-sm font-semibold">
                            {{ getString(child.data.title, 'Plan') }}
                          </div>
                          <div class="text-xs text-gray-500">
                            {{ getString(child.data.price, '$0') }}
                          </div>
                        </div>
                        <div
                          v-else-if="child.type === 'cta'"
                          class="space-y-2"
                        >
                          <div class="text-sm font-semibold">
                            {{ getString(child.data.text, 'CTA') }}
                          </div>
                          <span class="inline-flex items-center rounded-md bg-black px-3 py-1.5 text-[11px] font-semibold text-white">
                            {{ getString(child.data.buttonText, 'Apply Now') }}
                          </span>
                        </div>
                        <div
                          v-else-if="child.type === 'stats'"
                          class="grid gap-2"
                          :class="gridClass(child.data.layout, 'grid2')"
                        >
                          <div
                            v-for="(item, idx) in splitLines(child.data.items)"
                            :key="`cstat-${child.id}-${idx}`"
                            class="rounded-md bg-gray-50 p-2 text-[11px]"
                          >
                            <div class="font-semibold text-gray-800">
                              {{ splitPipe(item)[0] }}
                            </div>
                            <div class="text-[10px] text-gray-500">
                              {{ splitPipe(item)[1] }}
                            </div>
                          </div>
                        </div>
                        <div v-else-if="child.type === 'timeline'">
                          <div class="text-xs text-gray-600">
                            {{ splitPipe(getFirstLine(child.data.items))[0] }}
                          </div>
                        </div>
                        <div v-else-if="child.type === 'testimonials'">
                          <div class="text-xs text-gray-600">
                            Testimonial
                          </div>
                        </div>
                        <div v-else-if="child.type === 'team'">
                          <div class="text-xs text-gray-600">
                            Team member
                          </div>
                        </div>
                        <div v-else-if="child.type === 'feature_list'">
                          <div class="text-xs font-semibold text-gray-700">
                            {{ getString(child.data.title, 'Feature list') }}
                          </div>
                          <ul class="mt-1 list-disc pl-4 text-[11px] text-gray-500">
                            <li
                              v-for="(item, idx) in splitLines(child.data.items)"
                              :key="`cfeat-${child.id}-${idx}`"
                            >
                              {{ item }}
                            </li>
                          </ul>
                        </div>
                        <div
                          v-else-if="child.type === 'gallery'"
                          class="grid gap-2"
                          :class="gridClass(child.data.layout)"
                        >
                          <div
                            v-for="(img, idx) in getGalleryImagesWithFallback(child.data.images)"
                            :key="`cgal-${child.id}-${idx}`"
                            class="flex min-h-[60px] items-center justify-center rounded-md bg-gray-50 text-[10px] text-gray-400 overflow-hidden"
                          >
                            <img
                              v-if="img"
                              :src="img"
                              alt="Gallery item"
                              class="h-full w-full object-cover"
                            >
                            <span v-else>Image</span>
                          </div>
                        </div>
                        <div
                          v-else-if="child.type === 'video'"
                          class="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-[11px] text-gray-600"
                        >
                          ▶ {{ getString(child.data.caption, 'Video') }}
                        </div>
                        <div
                          v-else-if="child.type === 'logo_cloud'"
                          class="grid gap-2"
                          :class="gridClass(child.data.layout)"
                        >
                          <div
                            v-for="(item, idx) in splitLines(child.data.logos)"
                            :key="`clogo-${child.id}-${idx}`"
                            class="flex h-10 items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 text-[10px] text-gray-500"
                          >
                            {{ splitPipe(item)[0] || 'Logo' }}
                          </div>
                        </div>
                        <div v-else-if="child.type === 'steps'">
                          <div class="text-xs font-semibold text-gray-700">
                            {{ getString(child.data.title, 'Steps') }}
                          </div>
                          <ol class="mt-1 space-y-1 text-[11px] text-gray-600">
                            <li
                              v-for="(item, idx) in splitLines(child.data.steps)"
                              :key="`cstep-${child.id}-${idx}`"
                            >
                              {{ splitPipe(item)[0] }} {{ splitPipe(item)[1] ? '—' : '' }} {{ splitPipe(item)[1] }}
                            </li>
                          </ol>
                        </div>
                        <div
                          v-else-if="child.type === 'form'"
                          class="rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-[11px] text-gray-600"
                        >
                          {{ getString(child.data.title, 'Form') }}
                        </div>
                        <div v-else-if="child.type === 'hero'">
                          <div class="text-xs text-gray-600">
                            {{ getString(child.data.headline, 'Hero') }}
                          </div>
                        </div>
                        <div v-else-if="child.type === 'faq'">
                          <div class="text-xs text-gray-600">
                            FAQ
                          </div>
                        </div>
                        <div
                          v-else
                          class="text-xs text-gray-500"
                        >
                          {{ blockLabel(child.type) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="element.type === 'gallery'"
              :class="gridClass(element.data.layout)"
            >
              <div
                v-for="(img, idx) in getGalleryImagesWithFallback(element.data.images)"
                :key="`gal-${index}-${idx}`"
                class="flex min-h-[90px] items-center justify-center rounded-lg bg-gray-50 text-xs text-gray-400 overflow-hidden"
              >
                <img
                  v-if="img"
                  :src="img"
                  alt="Gallery item"
                  class="h-full w-full object-cover"
                >
                <span v-else>Image</span>
              </div>
            </div>
            <div v-else-if="element.type === 'video'">
              <div class="flex items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                <span class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-700">
                  ▶
                </span>
                <div>
                  <div class="font-semibold">
                    {{ getString(element.data.caption, 'Video') }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ getString(element.data.url, 'Video URL') }}
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else-if="element.type === 'logo_cloud'"
              :class="gridClass(element.data.layout)"
            >
              <div
                v-for="(item, idx) in splitLines(element.data.logos)"
                :key="`logo-${index}-${idx}`"
                class="flex h-16 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 text-xs text-gray-500"
              >
                {{ splitPipe(item)[0] || 'Logo' }}
              </div>
            </div>
            <div v-else-if="element.type === 'steps'">
              <div class="text-lg font-semibold">
                {{ getString(element.data.title, 'Steps') }}
              </div>
              <ol class="mt-2 space-y-2 text-sm text-gray-700">
                <li
                  v-for="(item, idx) in splitLines(element.data.steps)"
                  :key="`step-${index}-${idx}`"
                >
                  <span class="font-semibold">{{ splitPipe(item)[0] }}</span> — {{ splitPipe(item)[1] }}
                </li>
              </ol>
            </div>
            <div
              v-else-if="element.type === 'form'"
              class="space-y-3"
            >
              <div class="text-lg font-semibold">
                {{ getString(element.data.title, 'Form') }}
              </div>
              <div class="text-sm text-gray-600">
                {{ getString(element.data.description, '') }}
              </div>
              <div
                class="grid gap-2"
                :class="gridClass(element.data.layout, 'stack')"
              >
                <div
                  v-for="(field, idx) in getFormFields(element.data.fields)"
                  :key="`field-${index}-${idx}`"
                  class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-xs text-gray-500"
                >
                  {{ field.label || 'Field' }}
                </div>
              </div>
              <span class="inline-flex items-center rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white">
                {{ getString(element.data.buttonText, 'Send') }}
              </span>
            </div>
            <div v-else-if="element.type === 'feature_list'">
              <div class="text-lg font-semibold">
                {{ getString(element.data.title, 'Feature list') }}
              </div>
              <ul
                class="mt-2 list-disc pl-5 text-sm text-gray-700"
                :class="listGridClass(element.data.layout)"
              >
                <li
                  v-for="(item, idx) in splitLines(element.data.items)"
                  :key="`feat-${index}-${idx}`"
                >
                  {{ item }}
                </li>
              </ul>
            </div>
            <div
              v-else-if="element.type === 'stats'"
              :class="gridClass(element.data.layout)"
            >
              <div
                v-for="(item, idx) in splitLines(element.data.items)"
                :key="`stat-${index}-${idx}`"
                class="rounded-lg bg-gray-50 p-3"
              >
                <div class="text-lg font-semibold">
                  {{ splitPipe(item)[0] }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ splitPipe(item)[1] }}
                </div>
              </div>
            </div>
            <div
              v-else-if="element.type === 'timeline'"
              class="space-y-3"
            >
              <div
                v-for="(item, idx) in splitLines(element.data.items)"
                :key="`tl-${index}-${idx}`"
                class="rounded-lg bg-gray-50 p-3 text-sm text-gray-700"
              >
                <div class="font-semibold">
                  {{ splitPipe(item)[0] }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ splitPipe(item)[1] }}
                </div>
              </div>
            </div>
            <div
              v-else-if="element.type === 'pricing'"
              :class="gridClass(element.data.layout)"
            >
              <div class="rounded-lg bg-gray-50 p-4 text-sm text-gray-700">
                <div class="text-lg font-semibold">
                  {{ getString(element.data.title, 'Plan') }}
                </div>
                <div class="text-sm text-gray-500">
                  {{ getString(element.data.price, '$0') }}
                </div>
                <ul class="mt-2 list-disc pl-5">
                  <li
                    v-for="(item, idx) in splitLines(element.data.features)"
                    :key="`price-${index}-${idx}`"
                  >
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
            <div
              v-else-if="element.type === 'cta'"
              class="rounded-lg bg-gray-50 p-4 text-sm text-gray-700"
            >
              <div class="text-lg font-semibold">
                {{ getString(element.data.text, 'Call to action') }}
              </div>
              <span class="mt-2 inline-flex items-center rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white">
                {{ getString(element.data.buttonText, 'Apply Now') }}
              </span>
            </div>
            <div
              v-else-if="element.type === 'testimonials'"
              :class="gridClass(element.data.layout, 'stack')"
            >
              <div
                v-for="(item, idx) in splitLines(element.data.items)"
                :key="`test-${index}-${idx}`"
                class="rounded-lg bg-gray-50 p-3 text-sm text-gray-700"
              >
                <div class="italic">
                  “{{ splitPipe(item)[0] }}”
                </div>
                <div class="mt-1 text-xs text-gray-500">
                  — {{ splitPipe(item)[1] }}
                </div>
              </div>
            </div>
            <div
              v-else-if="element.type === 'team'"
              :class="gridClass(element.data.layout, 'grid2')"
            >
              <div
                v-for="(item, idx) in splitLines(element.data.members)"
                :key="`team-${index}-${idx}`"
                class="rounded-lg bg-gray-50 p-3 text-sm text-gray-700"
              >
                <div class="font-semibold">
                  {{ splitPipe(item)[0] }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ splitPipe(item)[1] }}
                </div>
              </div>
            </div>
            <div
              v-else-if="element.type === 'faq'"
              class="space-y-3"
            >
              <div
                v-for="(item, idx) in splitLines(element.data.items)"
                :key="`faq-${index}-${idx}`"
                class="rounded-lg bg-gray-50 p-3 text-sm text-gray-700"
              >
                <div class="font-semibold">
                  {{ splitPipe(item)[0] }}
                </div>
                <div class="text-xs text-gray-500">
                  {{ splitPipe(item)[1] }}
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-sm text-gray-500"
            >
              {{ blockLabel(element.type) }}
            </div>
          </div>
        </div>
        <div
          v-if="dropIndex === blocks.length && draggingIndex !== null"
          class="h-10 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref, watch} from 'vue';

type BlockType =
  | 'heading'
  | 'text'
  | 'image'
  | 'button'
  | 'quote'
  | 'embed'
  | 'divider'
  | 'html'
  | 'hero'
  | 'columns'
  | 'gallery'
  | 'video'
  | 'logo_cloud'
  | 'steps'
  | 'form'
  | 'faq'
  | 'timeline'
  | 'pricing'
  | 'cta'
  | 'stats'
  | 'team'
  | 'testimonials'
  | 'feature_list';

interface Block {
  id: string;
  type: BlockType;
  data: Record<string, unknown>;
}

interface BuilderState {
  version: number;
  blocks: Block[];
}

const createId = (): string => `blk_${Math.random().toString(36).slice(2, 10)}`;

const createBlock = (type: BlockType): Block => {
  const base = {id: createId(), type, data: {} as Record<string, unknown>};
  switch (type) {
    case 'heading':
      return {...base, data: {text: 'Heading', level: 2, align: 'left'}};
    case 'text':
      return {...base, data: {text: 'This is a text block. Click Edit to change it.', align: 'left'}};
    case 'image':
      return {...base, data: {src: '', alt: 'Image', caption: 'Image caption'}};
    case 'button':
      return {...base, data: {text: 'Call to Action', href: '#', variant: 'primary', align: 'left'}};
    case 'quote':
      return {...base, data: {text: 'Quote goes here.', author: 'Author'}};
    case 'embed':
      return {...base, data: {url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'}};
    case 'divider':
      return {...base, data: {style: 'solid', spacing: 24}};
    case 'html':
      return {...base, data: {html: '<div>Custom HTML</div>'}};
    case 'hero':
      return {
        ...base,
        data: {
          headline: 'Build a stronger network',
          subheadline: 'A short hero statement that explains the value.',
          ctaText: 'Get Started',
          ctaUrl: '#',
          align: 'left',
        },
      };
    case 'columns':
      return {
        ...base,
        data: {
          columns: 2,
          gap: 16,
          items: [[createBlock('text')], [createBlock('text')]],
        },
      };
    case 'gallery':
      return {
        ...base,
        data: {
          images: [],
          layout: 'grid3',
        },
      };
    case 'video':
      return {
        ...base,
        data: {
          url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          caption: 'Watch the story',
        },
      };
    case 'logo_cloud':
      return {
        ...base,
        data: {
          logos: 'Logo One|https://placehold.co/160x80\nLogo Two|https://placehold.co/160x80\nLogo Three|https://placehold.co/160x80',
          layout: 'grid3',
        },
      };
    case 'steps':
      return {
        ...base,
        data: {
          title: 'How it works',
          steps: '1|Submit your application\n2|Get vetted by the team\n3|Join the community',
          layout: 'stack',
        },
      };
    case 'form':
      return {
        ...base,
        data: {
          title: 'Get in touch',
          description: 'Tell us what you need and we will respond quickly.',
          fields: [
            {id: createId(), label: 'Full name', type: 'text', required: true},
            {id: createId(), label: 'Email', type: 'email', required: true},
            {id: createId(), label: 'Message', type: 'textarea', required: true},
          ],
          buttonText: 'Send message',
          layout: 'stack',
        },
      };
    case 'faq':
      return {
        ...base,
        data: {
          items: 'What is NevCap?|A private community for founders.\nHow do I join?|Request an invite.',
        },
      };
    case 'timeline':
      return {
        ...base,
        data: {
          items: '2024|Launched the community\n2025|Reached 1,000 members',
        },
      };
    case 'pricing':
      return {
        ...base,
        data: {
          title: 'Community Plus',
          price: '$49/mo',
          features: 'Investor directory\nEvents access\nMember perks',
          layout: 'stack',
        },
      };
    case 'cta':
      return {
        ...base,
        data: {
          text: 'Ready to meet your next investors?',
          buttonText: 'Apply Now',
          buttonUrl: '#',
        },
      };
    case 'stats':
      return {
        ...base,
        data: {
          items: '500+|Members\n24|Monthly events\n12|Countries',
          layout: 'grid3',
        },
      };
    case 'team':
      return {
        ...base,
        data: {
          members: 'Jane Doe|Founder\nJohn Smith|Community Lead',
          layout: 'grid2',
        },
      };
    case 'testimonials':
      return {
        ...base,
        data: {
          items: '“Amazing network.”|Jane Doe\n“High quality events.”|John Smith',
          layout: 'stack',
        },
      };
    case 'feature_list':
      return {
        ...base,
        data: {
          title: 'What you get',
          items: 'Curated community\nPrivate events\nMember directory',
          layout: 'stack',
        },
      };
    default:
      return base;
  }
};

const normalizeBlock = (raw: any): Block => {
  const block: Block = {
    id: raw?.id || createId(),
    type: raw?.type,
    data: raw?.data || {},
  };
  if (block.type === 'gallery') {
    const imagesRaw = (block.data as any).images;
    let images: string[] = [];
    if (Array.isArray(imagesRaw)) {
      images = imagesRaw.filter((img) => typeof img === 'string');
    } else if (typeof imagesRaw === 'string') {
      images = imagesRaw
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);
    }
    block.data = {
      ...block.data,
      images,
    };
  }
  if (block.type === 'columns') {
    const columns = Math.min(Math.max(Number(block.data.columns) || 2, 2), 4);
    const gap = Number(block.data.gap);
    const itemsRaw = Array.isArray(block.data.items) ? block.data.items : [];
    const items = itemsRaw.map((col: any[]) => (Array.isArray(col) ? col.map(normalizeBlock) : []));
    while (items.length < columns) items.push([]);
    block.data = {
      ...block.data,
      columns,
      gap: Number.isNaN(gap) || gap <= 0 ? 16 : gap,
      items,
    };
  }
  return block;
};

const normalizeState = (value: unknown): BuilderState => {
  if (value && typeof value === 'object' && Array.isArray((value as BuilderState).blocks)) {
    const state = value as BuilderState;
    const blocks = state.blocks.map((block) => normalizeBlock(block));
    return {version: state.version || 1, blocks};
  }
  return {version: 1, blocks: []};
};

export default defineComponent({
  name: 'PageBuilder',
  props: {
    modelValue: {
      type: Object,
      default: () => ({version: 1, blocks: []}),
    },
    selectedId: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'select'],
  setup(props, {emit}) {
    const blocks = ref<Block[]>([]);
    const newBlockType = ref<BlockType | ''>('');
    const draggingIndex = ref<number | null>(null);
    const dropIndex = ref<number | null>(null);
    const columnDrag = ref<{columnsId: string; colIndex: number; itemId: string} | null>(null);
    let isSyncing = false;

    const syncFromParent = (value: unknown) => {
      if (isSyncing) return;
      const normalized = normalizeState(value);
      blocks.value = normalized.blocks.filter((block) => block && typeof block === 'object');
    };

    watch(
      () => props.modelValue,
      (value) => syncFromParent(value),
      {immediate: true, deep: true}
    );

    watch(
      blocks,
      (value) => {
        isSyncing = true;
        emit('update:modelValue', {version: 1, blocks: value});
        setTimeout(() => {
          isSyncing = false;
        }, 0);
      },
      {deep: true}
    );

  const blockOptions = ref([
    {value: 'heading', label: 'Heading'},
    {value: 'text', label: 'Text'},
    {value: 'image', label: 'Image'},
    {value: 'button', label: 'Button'},
    {value: 'quote', label: 'Quote'},
    {value: 'embed', label: 'Embed (YouTube)'},
    {value: 'divider', label: 'Divider'},
    {value: 'html', label: 'HTML'},
    {value: 'hero', label: 'Hero'},
    {value: 'columns', label: 'Columns'},
    {value: 'gallery', label: 'Gallery'},
    {value: 'video', label: 'Video'},
    {value: 'logo_cloud', label: 'Logo Cloud'},
    {value: 'steps', label: 'Steps'},
    {value: 'form', label: 'Form Builder'},
    {value: 'feature_list', label: 'Feature List'},
    {value: 'stats', label: 'Stats'},
    {value: 'timeline', label: 'Timeline'},
    {value: 'pricing', label: 'Pricing'},
    {value: 'cta', label: 'CTA'},
    {value: 'testimonials', label: 'Testimonials'},
    {value: 'team', label: 'Team'},
    {value: 'faq', label: 'FAQ'},
  ] as {value: BlockType; label: string}[]);

  const addBlock = (type?: BlockType) => {
    const blockType = type || newBlockType.value;
    if (!blockType) return;
    blocks.value.push(createBlock(blockType));
    newBlockType.value = '';
  };

  const addBlockAt = (type: BlockType, index: number) => {
    const block = createBlock(type);
    const safeIndex = Math.min(Math.max(index, 0), blocks.value.length);
    blocks.value.splice(safeIndex, 0, block);
  };

    const removeBlock = (index: number) => {
      blocks.value.splice(index, 1);
    };

  const duplicateBlock = (index: number) => {
      const original = blocks.value[index];
      const clone: Block = {
        ...original,
        id: createId(),
        data: {...original.data},
      };
      blocks.value.splice(index + 1, 0, clone);
  };

  const moveBlock = (index: number, direction: number) => {
    const target = index + direction;
    if (target < 0 || target >= blocks.value.length) return;
    const [item] = blocks.value.splice(index, 1);
    blocks.value.splice(target, 0, item);
  };

  const onDragStart = (event: DragEvent, type: BlockType) => {
    if (!event.dataTransfer) return;
    event.dataTransfer.setData('application/x-block-type', type);
    event.dataTransfer.effectAllowed = 'copy';
  };

  const onDrop = (event: DragEvent) => {
    const type = event.dataTransfer?.getData('application/x-block-type') as BlockType | undefined;
    if (!type) return;
    const target = dropIndex.value ?? blocks.value.length;
    addBlockAt(type, target);
    dropIndex.value = null;
  };

  const onBlockDragStart = (event: DragEvent, index: number) => {
    if (!event.dataTransfer) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-column-inner]')) {
      event.preventDefault();
      return;
    }
    draggingIndex.value = index;
    event.dataTransfer.setData('application/x-block-source', 'root');
    event.dataTransfer.setData('application/x-block-index', String(index));
    event.dataTransfer.effectAllowed = 'move';
    const blockEl = target?.closest('[data-block]') as HTMLElement | null;
    const previewEl = target?.closest('[data-drag-preview]') as HTMLElement | null;
    const sourceEl = previewEl || blockEl;
    if (sourceEl) {
      const ghost = sourceEl.cloneNode(true) as HTMLElement;
      ghost.querySelectorAll('[data-drag-hide]').forEach((el) => el.remove());
      ghost.style.position = 'fixed';
      ghost.style.top = '-9999px';
      ghost.style.left = '-9999px';
      const rect = sourceEl.getBoundingClientRect();
      ghost.style.width = `${rect.width}px`;
      ghost.style.height = `${rect.height}px`;
      ghost.style.pointerEvents = 'none';
      ghost.style.opacity = '0.85';
      ghost.style.background = 'white';
      ghost.style.border = '1px dashed rgba(17,24,39,0.25)';
      ghost.style.boxShadow = '0 10px 30px rgba(15,23,42,0.12)';
      ghost.style.boxSizing = 'border-box';
      document.body.appendChild(ghost);
      const offsetX = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
      const offsetY = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
      event.dataTransfer.setDragImage(ghost, offsetX, offsetY);
      setTimeout(() => ghost.remove(), 0);
    }
  };

  const onBlockDragEnd = () => {
    draggingIndex.value = null;
    dropIndex.value = null;
    columnDrag.value = null;
  };

  const moveBlockTo = (from: number, toOriginal: number) => {
    if (from < 0 || from >= blocks.value.length) return;
    const bounded = Math.min(Math.max(toOriginal, 0), blocks.value.length);
    const toFinal = bounded > from ? bounded - 1 : bounded;
    if (from === toFinal) return;
    const [item] = blocks.value.splice(from, 1);
    blocks.value.splice(toFinal, 0, item);
  };

  const onBlockDrop = (event: DragEvent, index: number) => {
    event.stopPropagation();
    dropIndex.value = index;
    const paletteType = event.dataTransfer?.getData('application/x-block-type') as BlockType | undefined;
    if (paletteType) {
      const target = dropIndex.value ?? index;
      addBlockAt(paletteType, target);
      dropIndex.value = null;
      return;
    }
    const source = event.dataTransfer?.getData('application/x-block-source');
    const fromRaw = event.dataTransfer?.getData('application/x-block-index');
    if (source === 'root' && fromRaw != null && fromRaw !== '') {
      const from = Number(fromRaw);
      if (Number.isNaN(from)) return;
      const target = dropIndex.value ?? index;
      moveBlockTo(from, target);
      dropIndex.value = null;
      return;
    }
    if (source === 'column' && fromRaw != null && fromRaw !== '') {
      const from = Number(fromRaw);
      if (Number.isNaN(from)) return;
      const target = dropIndex.value ?? index;
      moveColumnBlockToRoot(event, from, target);
      dropIndex.value = null;
      return;
    }
  };

  const onCanvasDrop = (event: DragEvent) => {
    const source = event.dataTransfer?.getData('application/x-block-source');
    const fromRaw = event.dataTransfer?.getData('application/x-block-index');
    if (source === 'root' && fromRaw != null && fromRaw !== '') {
      const from = Number(fromRaw);
      if (!Number.isNaN(from)) {
        const target = dropIndex.value ?? blocks.value.length;
        moveBlockTo(from, target);
        dropIndex.value = null;
        return;
      }
    }
    if (source === 'column' && fromRaw != null && fromRaw !== '') {
      const from = Number(fromRaw);
      if (!Number.isNaN(from)) {
        const target = dropIndex.value ?? blocks.value.length;
        moveColumnBlockToRoot(event, from, target);
        dropIndex.value = null;
        return;
      }
    }
    onDrop(event);
  };

    const onCanvasDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (draggingIndex.value !== null || event.dataTransfer?.getData('application/x-block-type')) {
        dropIndex.value = blocks.value.length;
      }
    };

  const onBlockDragOver = (event: DragEvent, index: number) => {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      dropIndex.value = index;
      return;
    }
    const rect = target.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const nextIndex = event.clientY > mid ? index + 1 : index;
    dropIndex.value = nextIndex;
    const paletteType = event.dataTransfer?.getData('application/x-block-type');
    if (paletteType) {
      return;
    }
    if (draggingIndex.value !== null && draggingIndex.value !== nextIndex) {
      moveBlockTo(draggingIndex.value, nextIndex);
      draggingIndex.value = nextIndex > draggingIndex.value ? nextIndex - 1 : nextIndex;
    }
  };

  const getColumnsRender = (block: Block) => {
    const data = block.data as Record<string, unknown>;
    let columns = Math.min(Math.max(Number(data.columns) || 2, 2), 4);
    let items = Array.isArray(data.items) ? (data.items as Block[][]) : [];
    items = items.map((col) => (Array.isArray(col) ? col : []));
    while (items.length < columns) items.push([]);
    if (items.length > columns) items = items.slice(0, columns);
    return {columns, items, gap: Number(data.gap) || 16};
  };

  const ensureColumnsData = (block: Block) => {
    const data = block.data as Record<string, unknown>;
    const normalized = getColumnsRender(block);
    data.columns = normalized.columns;
    data.gap = Number(data.gap) || 16;
    if (!Array.isArray(data.items)) {
      data.items = normalized.items;
    } else if ((data.items as Block[][]).length !== normalized.items.length) {
      data.items = normalized.items;
    }
    return {columns: normalized.columns, gap: Number(data.gap) || 16, items: data.items as Block[][]};
  };

  const getColumns = (block: Block) => getColumnsRender(block).items;

  const columnsGridClass = (block: Block) => {
    const cols = getColumnsRender(block).columns;
    if (cols === 2) return 'md:grid-cols-2';
    if (cols === 3) return 'md:grid-cols-3';
    if (cols === 4) return 'md:grid-cols-4';
    return 'md:grid-cols-2';
  };

  const getColumnsGap = (block: Block) => getColumnsRender(block).gap;

  const onColumnDrop = (event: DragEvent, columnsId: string, colIndex: number) => {
    event.stopPropagation();
    const type = event.dataTransfer?.getData('application/x-block-type') as BlockType | undefined;
    if (type) {
      addBlockToColumn(columnsId, colIndex, createBlock(type));
      dropIndex.value = null;
      return;
    }
    const source = event.dataTransfer?.getData('application/x-block-source');
    const fromRaw = event.dataTransfer?.getData('application/x-block-index');
    if (source === 'root' && fromRaw != null && fromRaw !== '') {
      const from = Number(fromRaw);
      if (Number.isNaN(from)) return;
      moveRootBlockToColumn(from, columnsId, colIndex);
      dropIndex.value = null;
      return;
    }
    if (source === 'column' && fromRaw != null && fromRaw !== '') {
      const from = Number(fromRaw);
      if (Number.isNaN(from)) return;
      moveColumnBlock(event, columnsId, colIndex, from);
      dropIndex.value = null;
    }
  };

  const onColumnBlockDragStart = (
    event: DragEvent,
    columnsId: string,
    colIndex: number,
    childIndex: number
  ) => {
    if (!event.dataTransfer) return;
    event.stopPropagation();
    event.dataTransfer.setData('application/x-block-source', 'column');
    event.dataTransfer.setData('application/x-block-index', String(childIndex));
    event.dataTransfer.setData('application/x-block-col', String(colIndex));
    event.dataTransfer.setData('application/x-block-columns-id', columnsId);
    event.dataTransfer.effectAllowed = 'move';
    const columnsBlock = blocks.value.find((b) => b.id === columnsId);
    if (columnsBlock) {
      const data = ensureColumnsData(columnsBlock);
      const item = data.items[colIndex]?.[childIndex];
      if (item) {
        event.dataTransfer.setData('application/x-block-item-id', item.id);
        columnDrag.value = {columnsId, colIndex, itemId: item.id};
      }
    }
  };

  const onColumnBlockDrop = (
    event: DragEvent,
    columnsId: string,
    colIndex: number,
    childIndex: number
  ) => {
    event.stopPropagation();
    columnDrag.value = null;
    const source = event.dataTransfer?.getData('application/x-block-source');
    if (source !== 'column') {
      onColumnDrop(event, columnsId, colIndex);
      return;
    }
    const fromRaw = event.dataTransfer?.getData('application/x-block-index');
    if (fromRaw == null || fromRaw === '') return;
    const from = Number(fromRaw);
    if (Number.isNaN(from)) return;
    const sourceColumnsId = event.dataTransfer?.getData('application/x-block-columns-id');
    const sourceColRaw = event.dataTransfer?.getData('application/x-block-col');
    if (sourceColumnsId && sourceColRaw != null) {
      const sourceCol = Number(sourceColRaw);
      if (!Number.isNaN(sourceCol)) {
        if (sourceColumnsId === columnsId && sourceCol === colIndex) {
          return;
        }
      }
    }
    moveColumnBlock(event, columnsId, colIndex, from, childIndex);
  };

  const onColumnBlockDragOver = (
    event: DragEvent,
    columnsId: string,
    colIndex: number,
    childIndex: number
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    const source = event.dataTransfer?.getData('application/x-block-source');
    if (source !== 'column') return;
    if (!columnDrag.value) return;
    if (columnDrag.value.columnsId !== columnsId || columnDrag.value.colIndex !== colIndex) return;
    const columnsBlock = blocks.value.find((b) => b.id === columnsId);
    if (!columnsBlock) return;
    const data = ensureColumnsData(columnsBlock);
    const currentIndex = data.items[colIndex].findIndex((item) => item.id === columnDrag.value?.itemId);
    if (currentIndex === -1) return;
    const target = childIndex;
    if (currentIndex === target) return;
    const [item] = data.items[colIndex].splice(currentIndex, 1);
    const insertAt = currentIndex < target ? target - 1 : target;
    data.items[colIndex].splice(insertAt, 0, item);
  };

  const addBlockToColumn = (columnsId: string, colIndex: number, block: Block) => {
    const columnsBlock = blocks.value.find((b) => b.id === columnsId);
    if (!columnsBlock) return;
    const data = ensureColumnsData(columnsBlock);
    data.items[colIndex].push(block);
  };

  const moveRootBlockToColumn = (from: number, columnsId: string, colIndex: number) => {
    const columnsBlock = blocks.value.find((b) => b.id === columnsId);
    if (!columnsBlock) return;
    const data = ensureColumnsData(columnsBlock);
    const [item] = blocks.value.splice(from, 1);
    data.items[colIndex].push(item);
  };

  const removeColumnBlock = (columnsId: string, colIndex: number, childIndex: number) => {
    const columnsBlock = blocks.value.find((b) => b.id === columnsId);
    if (!columnsBlock) return;
    const data = ensureColumnsData(columnsBlock);
    data.items[colIndex].splice(childIndex, 1);
  };

  const moveColumnBlock = (
    event: DragEvent,
    targetColumnsId: string,
    targetColIndex: number,
    fromIndex: number,
    insertIndex?: number
  ) => {
    const sourceColumnsId = event.dataTransfer?.getData('application/x-block-columns-id');
    const sourceColRaw = event.dataTransfer?.getData('application/x-block-col');
    if (!sourceColumnsId || sourceColRaw == null) return;
    const sourceCol = Number(sourceColRaw);
    if (Number.isNaN(sourceCol)) return;
    const sourceBlock = blocks.value.find((b) => b.id === sourceColumnsId);
    const targetBlock = blocks.value.find((b) => b.id === targetColumnsId);
    if (!sourceBlock || !targetBlock) return;
    const sourceData = ensureColumnsData(sourceBlock);
    const targetData = ensureColumnsData(targetBlock);
    let actualIndex = fromIndex;
    const itemId = event.dataTransfer?.getData('application/x-block-item-id');
    if (itemId) {
      const foundIndex = sourceData.items[sourceCol].findIndex((item) => item.id === itemId);
      if (foundIndex !== -1) {
        actualIndex = foundIndex;
      }
    }
    const [item] = sourceData.items[sourceCol].splice(actualIndex, 1);
    const insertAt =
      insertIndex == null ? targetData.items[targetColIndex].length : Math.max(0, insertIndex);
    targetData.items[targetColIndex].splice(insertAt, 0, item);
  };

  const moveColumnBlockButton = (columnsId: string, colIndex: number, childIndex: number, direction: number) => {
    const columnsBlock = blocks.value.find((b) => b.id === columnsId);
    if (!columnsBlock) return;
    const data = ensureColumnsData(columnsBlock);
    const target = childIndex + direction;
    if (target < 0 || target >= data.items[colIndex].length) return;
    const [item] = data.items[colIndex].splice(childIndex, 1);
    data.items[colIndex].splice(target, 0, item);
  };

  const moveColumnBlockToRoot = (event: DragEvent, fromIndex: number, insertAt: number) => {
    const sourceColumnsId = event.dataTransfer?.getData('application/x-block-columns-id');
    const sourceColRaw = event.dataTransfer?.getData('application/x-block-col');
    if (!sourceColumnsId || sourceColRaw == null) return;
    const sourceCol = Number(sourceColRaw);
    if (Number.isNaN(sourceCol)) return;
    const sourceBlock = blocks.value.find((b) => b.id === sourceColumnsId);
    if (!sourceBlock) return;
    const data = ensureColumnsData(sourceBlock);
    const [item] = data.items[sourceCol].splice(fromIndex, 1);
    const insertIndex = Math.min(Math.max(insertAt, 0), blocks.value.length);
    blocks.value.splice(insertIndex, 0, item);
  };

    const selectBlock = (id: string) => {
      emit('select', id);
    };

    const previewAlign = (align?: unknown) => ({
      'text-left': (align || 'left') === 'left',
      'text-center': align === 'center',
      'text-right': align === 'right',
    });

    const buttonPreviewClass = (variant?: unknown) => {
      const v = (variant as string) || 'primary';
      return {
        'bg-black text-white': v === 'primary',
        'bg-gray-100 text-gray-900': v === 'secondary',
        'border border-gray-300 text-gray-900': v === 'outline',
      };
    };

    const dividerPreviewClass = (style?: unknown) => {
      const s = (style as string) || 'solid';
      return {
        'border-t border-gray-200': s === 'solid',
        'border-t border-dashed border-gray-300': s === 'dashed',
        'border-t border-dotted border-gray-300': s === 'dotted',
      };
    };

    const headingClass = (level?: unknown) => {
      const value = Number(level) || 2;
      const map: Record<number, string> = {
        1: 'text-2xl font-semibold',
        2: 'text-xl font-semibold',
        3: 'text-lg font-semibold',
        4: 'text-base font-semibold',
      };
      return map[value] || 'text-xl font-semibold';
    };

    const inputAlignClass = (align?: unknown) => {
      return {
        'text-left': (align || 'left') === 'left',
        'text-center': align === 'center',
        'text-right': align === 'right',
      };
    };

    const getString = (value: unknown, fallback: string) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
      return fallback;
    };

    const splitLines = (value: unknown) => {
      if (typeof value !== 'string') return [];
      return value
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    };

    const getFirstLine = (value: unknown) => {
      const lines = splitLines(value);
      return lines[0] || '';
    };

    const getGalleryImages = (value: unknown) => {
      if (Array.isArray(value)) {
        return value.filter((img) => typeof img === 'string');
      }
      if (typeof value === 'string') {
        return splitLines(value);
      }
      return [];
    };

    const galleryFallback = () => [
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><rect width='100%25' height='100%25' fill='%23F3F4F6'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='20' fill='%239CA3AF'>Gallery Image</text></svg>",
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><rect width='100%25' height='100%25' fill='%23F3F4F6'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='20' fill='%239CA3AF'>Gallery Image</text></svg>",
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><rect width='100%25' height='100%25' fill='%23F3F4F6'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='20' fill='%239CA3AF'>Gallery Image</text></svg>",
    ];

    const getGalleryImagesWithFallback = (value: unknown) => {
      const images = getGalleryImages(value);
      return images.length ? images : galleryFallback();
    };

    const getFormFields = (value: unknown) => {
      if (Array.isArray(value)) {
        return value.filter((field) => field && typeof field === 'object');
      }
      if (typeof value === 'string') {
        return splitLines(value).map((line) => {
          const [label, type, required] = line.split('|').map((part) => part.trim());
          return {
            id: createId(),
            label: label || 'Field',
            type: type || 'text',
            required: required === 'required',
          };
        });
      }
      return [];
    };

    const splitPipe = (value: string) => {
      const [left, right] = value.split('|').map((part) => part.trim());
      return [left || '', right || ''];
    };

    const gridClass = (layout?: unknown, fallback: 'stack' | 'grid2' | 'grid3' = 'grid2') => {
      const value = (layout as string) || fallback;
      if (value === 'stack') return 'space-y-3';
      if (value === 'grid2') return 'grid gap-3 sm:grid-cols-2';
      if (value === 'grid3') return 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3';
      return 'space-y-3';
    };

    const listGridClass = (layout?: unknown) => {
      const value = (layout as string) || 'stack';
      if (value === 'grid2') return 'grid gap-2 sm:grid-cols-2';
      if (value === 'grid3') return 'grid gap-2 sm:grid-cols-2 lg:grid-cols-3';
      return '';
    };

    const iconPath = (type: BlockType) => {
      const map: Record<BlockType, string> = {
        heading: 'M4 6h16M4 12h10M4 18h6',
        text: 'M4 7h16M4 12h16M4 17h10',
        image: 'M4 6h16v12H4z M8 10l3 3 5-5 4 4',
        button: 'M4 10h16v4H4z',
        quote: 'M7 7h4v4H7z M13 7h4v4h-4z',
        embed: 'M8 7l-4 5 4 5 M16 7l4 5-4 5',
        divider: 'M4 12h16',
        html: 'M8 8l-3 4 3 4 M16 8l3 4-3 4',
        hero: 'M4 6h16M4 12h10M4 18h6',
        columns: 'M4 6h7v12H4z M13 6h7v12h-7z',
        gallery: 'M4 6h7v7H4z M13 6h7v7h-7z M4 15h7v3H4z M13 15h7v3h-7z',
        video: 'M4 6h16v12H4z M10 9l5 3-5 3',
        logo_cloud: 'M4 7h6v4H4z M14 7h6v4h-6z M4 13h6v4H4z M14 13h6v4h-6z',
        steps: 'M5 6h14M5 12h10M5 18h8',
        form: 'M4 6h16v12H4z M6 9h12 M6 13h8',
        feature_list: 'M6 7h12M6 12h12M6 17h12',
        stats: 'M6 16V8 M12 16V6 M18 16V10',
        timeline: 'M6 6v12 M6 6h10 M6 12h10 M6 18h10',
        pricing: 'M6 8h12 M6 12h8 M6 16h10',
        cta: 'M6 12h6 M12 9l4 3-4 3',
        testimonials: 'M4 7h16M4 12h10M4 17h8',
        team: 'M7 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6 M17 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6 M4 18c0-2 3-3 3-3h6s3 1 3 3',
        faq: 'M8 9a4 4 0 0 1 8 0c0 2-2 2-2 4 M12 17h.01',
      };
      return map[type] || 'M4 12h16';
    };

    const blockLabel = (type: BlockType) => {
      const map: Record<BlockType, string> = {
        heading: 'Heading',
        text: 'Text',
        image: 'Image',
        button: 'Button',
        quote: 'Quote',
        embed: 'Embed',
        divider: 'Divider',
        html: 'HTML',
        hero: 'Hero',
        columns: 'Columns',
        gallery: 'Gallery',
        video: 'Video',
        logo_cloud: 'Logo Cloud',
        steps: 'Steps',
        form: 'Form',
        faq: 'FAQ',
        timeline: 'Timeline',
        pricing: 'Pricing',
        cta: 'CTA',
        stats: 'Stats',
        team: 'Team',
        testimonials: 'Testimonials',
        feature_list: 'Feature List',
      };
      return map[type] || 'Block';
    };

    return {
      blocks,
      newBlockType,
      draggingIndex,
      dropIndex,
      columnDrag,
      blockOptions,
      addBlock,
      addBlockAt,
      removeBlock,
      duplicateBlock,
      moveBlock,
      blockLabel,
    onDragStart,
      onDrop,
      onBlockDragStart,
      onBlockDragEnd,
      onBlockDrop,
      onCanvasDrop,
      onCanvasDragOver,
      onBlockDragOver,
      onColumnDrop,
      onColumnBlockDragStart,
      onColumnBlockDrop,
      onColumnBlockDragOver,
      removeColumnBlock,
      moveColumnBlockButton,
      getColumns,
      columnsGridClass,
      getColumnsGap,
      selectBlock,
      previewAlign,
      buttonPreviewClass,
      dividerPreviewClass,
      headingClass,
      inputAlignClass,
      getString,
      splitLines,
      getFirstLine,
      getGalleryImages,
      getGalleryImagesWithFallback,
      getFormFields,
      splitPipe,
      gridClass,
      listGridClass,
      iconPath,
    };
  },
});
</script>
